import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import DeleteButton from '../components/DeleteButton';
import EntityDetailLayout from '../components/EntityDetailLayout';
import EnrichmentPanel from '../components/enrichment/EnrichmentPanel';
import EntityPivotLink from '../components/EntityPivotLink';
import InvestigationGraph from '../components/InvestigationGraph';
import RelationshipTable from '../components/RelationshipTable';
import useEvidenceTimezone from '../hooks/useEvidenceTimezone';
import { createSearch, fetchSearch } from '../services/intelligenceApi';
import { downloadAuthenticatedFile, uploadMultipart } from '../services/hdpFiles';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'enrichment', label: 'Enrichment' },
  { id: 'sightings', label: 'Sightings' },
  { id: 'raw', label: 'Raw' },
];

function IndicatorDetail() {
  const { indicatorId } = useParams();
  const navigate = useNavigate();
  const { format, suffix } = useEvidenceTimezone();
  const [activeTab, setActiveTab] = useState('overview');
  const [indicator, setIndicator] = useState(null);
  const [linkedSearch, setLinkedSearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [graphOpen, setGraphOpen] = useState(false);
  const [sightingNotes, setSightingNotes] = useState('');
  const [savingSighting, setSavingSighting] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [editingSourceUrl, setEditingSourceUrl] = useState(false);
  const [savingSourceUrl, setSavingSourceUrl] = useState(false);
  const [uploadingSource, setUploadingSource] = useState(false);
  const [sourceError, setSourceError] = useState('');

  const addSighting = async () => {
    if (!indicator) return;
    setSavingSighting(true);
    try {
      const res = await api.patch(`/hdp/iocs/${indicatorId}`, {
        sighting: { notes: sightingNotes, source: 'analyst' },
      });
      setIndicator(res.data);
      setSightingNotes('');
    } catch (err) {
      console.error(err);
    } finally {
      setSavingSighting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/hdp/iocs/${indicatorId}`);
        setIndicator(res.data);
        const link = res.data.source_link || {};
        setSourceUrl(res.data.source_url || link.url || '');
        setSourceError('');
        const searchId = res.data.metadata?.search_query_id;
        if (searchId) {
          try {
            const searchRes = await fetchSearch(searchId);
            setLinkedSearch(searchRes.data);
          } catch {
            setLinkedSearch(null);
          }
        } else {
          setLinkedSearch(null);
        }
      } catch (err) {
        console.error(err);
        setIndicator(null);
        setLinkedSearch(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [indicatorId]);

  const saveSourceUrl = async () => {
    setSavingSourceUrl(true);
    setSourceError('');
    try {
      const res = await api.patch(`/hdp/iocs/${indicatorId}`, {
        source_url: sourceUrl.trim() || null,
      });
      setIndicator(res.data);
      setEditingSourceUrl(false);
    } catch (err) {
      setSourceError(err.response?.data?.error || err.message || 'Failed to save source URL');
    } finally {
      setSavingSourceUrl(false);
    }
  };

  const uploadSourceAttachment = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingSource(true);
    setSourceError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await uploadMultipart(`/hdp/iocs/${indicatorId}/source-attachment`, formData);
      setIndicator(data);
      event.target.value = '';
    } catch (err) {
      setSourceError(err.message || 'Upload failed');
    } finally {
      setUploadingSource(false);
    }
  };

  const downloadSourceAttachment = async () => {
    const attachment = indicator?.source_attachment;
    if (!attachment?.download_url) return;
    try {
      await downloadAuthenticatedFile(
        attachment.download_url.replace('/api', ''),
        attachment.original_filename || attachment.title || 'source-attachment',
      );
    } catch (err) {
      setSourceError(err.message || 'Download failed');
    }
  };

  const removeSourceAttachment = async () => {
    setSourceError('');
    try {
      const res = await api.patch(`/hdp/iocs/${indicatorId}`, { source_attachment_id: null });
      setIndicator(res.data);
    } catch (err) {
      setSourceError(err.response?.data?.error || err.message || 'Failed to remove attachment');
    }
  };

  const runEnrichment = async () => {
    if (!indicator?.value) return;
    setEnriching(true);
    try {
      const res = await createSearch(indicator.value, true);
      navigate(`/intelligence/search/${res.data.query_id}`);
    } catch (err) {
      console.error(err);
    } finally {
      setEnriching(false);
    }
  };

  const enrichments = linkedSearch?.enrichments || {};
  const enrichmentEntries = Object.entries(enrichments).filter(([key]) => key !== 'error');
  const sourceLink = indicator?.source_link;
  const sourceMaterialUrl = indicator?.source_url || sourceLink?.url;
  const sourceMaterialLabel = sourceLink?.label || sourceLink?.title;

  const sidebar = indicator ? (
    <>
      <div className="entity-sidebar-section">
        <div className="entity-sidebar-title">Quick actions</div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          style={{ width: '100%', marginBottom: '8px' }}
          disabled={enriching}
          onClick={runEnrichment}
        >
          {enriching ? 'Starting...' : 'Run enrichment'}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          style={{ width: '100%', marginBottom: '8px' }}
          onClick={() => setGraphOpen(true)}
        >
          Show graph
        </button>
      </div>
      {(sourceMaterialLabel || sourceMaterialUrl) && (
        <div className="entity-sidebar-section">
          <div className="entity-sidebar-title">Source material</div>
          {sourceMaterialUrl ? (
            <a href={sourceMaterialUrl} target="_blank" rel="noopener noreferrer">
              {sourceMaterialLabel || sourceMaterialUrl}
            </a>
          ) : (
            <span>{sourceMaterialLabel}</span>
          )}
        </div>
      )}
      {indicator.case_id && (
        <div className="entity-sidebar-section">
          <div className="entity-sidebar-title">Import case</div>
          <EntityPivotLink
            type="case"
            id={indicator.case_id}
            label={indicator.case_title || indicator.case_id}
          />
        </div>
      )}
      {linkedSearch && (
        <div className="entity-sidebar-section">
          <div className="entity-sidebar-title">Source search</div>
          <EntityPivotLink
            type="search"
            id={linkedSearch.query_id}
            label={linkedSearch.raw_input}
          />
        </div>
      )}
      {indicator.tags?.length > 0 && (
        <div className="entity-sidebar-section">
          <div className="entity-sidebar-title">Tags</div>
          <div className="entity-tag-list">
            {indicator.tags.map((tag) => (
              <span key={tag} className="entity-tag">{tag}</span>
            ))}
          </div>
        </div>
      )}
    </>
  ) : null;

  return (
    <>
      <EntityDetailLayout
        entityType={indicator?.kind === 'observable' ? 'observable' : 'indicator'}
        title={indicator?.value || 'Indicator'}
        subtitle={indicator ? `${indicator.ioc_type} · ${indicator.confidence} confidence` : ''}
        breadcrumbs={[
          { label: indicator?.kind === 'observable' ? 'Observables' : 'Indicators', to: indicator?.kind === 'observable' ? '/observables' : '/indicators' },
          { label: indicator?.value || indicatorId },
        ]}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        loading={loading}
        notFound={!loading && !indicator ? 'Indicator not found.' : null}
        notFoundAction={<Link to="/indicators" className="btn btn-secondary">Back to Indicators</Link>}
        headerActions={
          indicator && (
            <>
              <span className="status-badge">{indicator.confidence}</span>
              <span className={`marking-pill marking-${(indicator.marking || 'TLP:CLEAR').replace(/[+:]/g, '-').toLowerCase()}`}>
                {indicator.marking || 'TLP:CLEAR'}
              </span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGraphOpen(true)}>
                Graph
              </button>
              <DeleteButton
                label="Delete"
                className="btn btn-sm btn-danger"
                confirmMessage={`Delete indicator "${indicator.value}"?`}
                onDelete={async () => {
                  await api.delete(`/hdp/iocs/${indicatorId}`);
                  navigate('/indicators');
                }}
              />
              {indicator.case_id && (
                <EntityPivotLink
                  type="case"
                  id={indicator.case_id}
                  label="Open case"
                  className="btn btn-secondary btn-sm"
                  showBadge={false}
                />
              )}
            </>
          )
        }
        sidebar={sidebar}
      >
        {indicator && activeTab === 'overview' && (
          <div className="card">
            <div className="card-body">
              <div className="entity-kv-grid">
                <div><strong>Kind</strong><div>{indicator.kind || 'indicator'}</div></div>
                <div><strong>Marking</strong><div>{indicator.marking || 'TLP:CLEAR'}</div></div>
                <div><strong>Type</strong><div>{indicator.ioc_type}</div></div>
                <div><strong>Value</strong><div><code>{indicator.value}</code></div></div>
                <div><strong>Normalized</strong><div><code>{indicator.normalized_value || '—'}</code></div></div>
                <div><strong>Catalog</strong><div>{indicator.source || '—'}</div></div>
                <div><strong>First seen ({suffix})</strong><div className="timestamp-cell">{format(indicator.first_seen)}</div></div>
                <div><strong>Last seen ({suffix})</strong><div className="timestamp-cell">{format(indicator.last_seen)}</div></div>
              </div>
              {indicator.description && <p style={{ marginTop: '16px' }}>{indicator.description}</p>}
              {indicator.notes && (
                <p style={{ marginTop: '12px', color: 'var(--hdp-text-muted)' }}>{indicator.notes}</p>
              )}

              <div className="entity-source-panel" style={{ marginTop: '24px' }}>
                <div className="entity-sidebar-title" style={{ marginBottom: '12px' }}>Source material</div>
                {sourceMaterialLabel && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{sourceMaterialLabel}</strong>
                  </div>
                )}
                {sourceLink?.individuals?.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ color: 'var(--hdp-text-muted)', fontSize: '13px' }}>Linked individuals: </span>
                    {sourceLink.individuals.join(', ')}
                  </div>
                )}
                {sourceLink?.citation && (
                  <p style={{ marginBottom: '12px', color: 'var(--hdp-text-muted)', fontSize: '13px' }}>
                    {sourceLink.citation}
                  </p>
                )}
                {sourceMaterialUrl && !editingSourceUrl && (
                  <div style={{ marginBottom: '12px' }}>
                    <a href={sourceMaterialUrl} target="_blank" rel="noopener noreferrer">
                      {sourceMaterialUrl}
                    </a>
                  </div>
                )}
                {editingSourceUrl ? (
                  <div className="sighting-form" style={{ marginBottom: '12px' }}>
                    <input
                      type="url"
                      className="form-input"
                      placeholder="https://..."
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={savingSourceUrl}
                        onClick={saveSourceUrl}
                      >
                        {savingSourceUrl ? 'Saving...' : 'Save URL'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setEditingSourceUrl(false);
                          setSourceUrl(sourceMaterialUrl || '');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ marginBottom: '12px' }}
                    onClick={() => setEditingSourceUrl(true)}
                  >
                    {sourceMaterialUrl ? 'Edit source URL' : 'Add source URL'}
                  </button>
                )}
                {indicator.source_attachment ? (
                  <div className="entity-related-chips" style={{ marginBottom: '12px' }}>
                    <span className="entity-tag">{indicator.source_attachment.title}</span>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={downloadSourceAttachment}>
                      Download
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={removeSourceAttachment}>
                      Remove
                    </button>
                  </div>
                ) : (
                  <div style={{ marginBottom: '12px' }}>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: uploadingSource ? 'wait' : 'pointer' }}>
                      {uploadingSource ? 'Uploading...' : 'Upload source attachment'}
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        disabled={uploadingSource}
                        onChange={uploadSourceAttachment}
                      />
                    </label>
                  </div>
                )}
                {sourceError && (
                  <p style={{ color: 'var(--hdp-danger, #c0392b)', fontSize: '13px' }}>{sourceError}</p>
                )}
              </div>

              {(indicator.case_id || indicator.actor_id) && (
                <div className="entity-related-chips" style={{ marginTop: '18px' }}>
                  {indicator.case_id && (
                    <EntityPivotLink
                      type="case"
                      id={indicator.case_id}
                      label={indicator.case_title || indicator.case_id}
                    />
                  )}
                  {indicator.actor_id && (
                    <EntityPivotLink
                      type="actor"
                      id={indicator.actor_id}
                      label={indicator.actor_name || indicator.actor_id}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {indicator && activeTab === 'relationships' && (
          <div className="card">
            <div className="card-body">
              <RelationshipTable entityType="indicator" entityId={indicatorId} />
            </div>
          </div>
        )}

        {indicator && activeTab === 'enrichment' && (
          <div>
            {!linkedSearch ? (
              <div className="card">
                <div className="card-body">
                  <p style={{ color: 'var(--hdp-text-muted)' }}>
                    No enrichment snapshot linked yet. Run a search on this indicator to populate enrichment data.
                  </p>
                  <button type="button" className="btn btn-primary btn-sm" disabled={enriching} onClick={runEnrichment}>
                    {enriching ? 'Starting...' : 'Run enrichment'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="card" style={{ marginBottom: '16px' }}>
                  <div className="card-body">
                    <div className="entity-related-chips">
                      <span style={{ color: 'var(--hdp-text-muted)', fontSize: '13px' }}>Linked search:</span>
                      <EntityPivotLink
                        type="search"
                        id={linkedSearch.query_id}
                        label={linkedSearch.raw_input}
                      />
                      {linkedSearch.risk_score != null && (
                        <span className="status-badge">risk {Math.round(linkedSearch.risk_score)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <EnrichmentPanel
                  enrichments={enrichments}
                  detectedType={linkedSearch?.detected_type || indicator?.ioc_type}
                  emptyMessage="Search linked but no enrichment sections yet."
                />
              </>
            )}
          </div>
        )}

        {indicator && activeTab === 'sightings' && (
          <div className="card">
            <div className="card-body">
              <div className="sighting-form">
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Add sighting notes..."
                  value={sightingNotes}
                  onChange={(e) => setSightingNotes(e.target.value)}
                />
                <button type="button" className="btn btn-primary btn-sm" disabled={savingSighting} onClick={addSighting}>
                  {savingSighting ? 'Saving...' : 'Record sighting'}
                </button>
              </div>
              <table className="data-table entity-table" style={{ marginTop: '16px' }}>
                <thead>
                  <tr>
                    <th>Seen at</th>
                    <th>Source</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(indicator.sightings || []).length === 0 ? (
                    <tr><td colSpan={3}>No sightings recorded.</td></tr>
                  ) : (indicator.sightings || []).map((item, index) => (
                    <tr key={`${item.seen_at}-${index}`}>
                      <td>{item.seen_at ? format(item.seen_at) : '—'}</td>
                      <td>{item.source || '—'}</td>
                      <td>{item.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {indicator && activeTab === 'raw' && (
          <div className="card">
            <div className="card-body">
              <pre className="entity-raw-json">{JSON.stringify(indicator, null, 2)}</pre>
            </div>
          </div>
        )}
      </EntityDetailLayout>

      <InvestigationGraph
        open={graphOpen}
        onClose={() => setGraphOpen(false)}
        entityType="indicator"
        entityId={indicatorId}
        title={indicator?.value}
      />
    </>
  );
}

export default IndicatorDetail;
